// 씬 관리 클래스 (조명, 바닥, 환경 오브젝트 등)
class SceneManager {
  constructor(scene, game = null) {
    this.scene = scene;
    this.game = game; // game 객체 참조 추가
    this.lights = {};
    this.environment = {};

    // 무한 스크롤을 위한 속성들
    this.groundTiles = [];
    this.tileSize = 20;
    this.viewDistance = 60;

    // 오브젝트 스폰을 위한 속성들
    this.environmentObjects = [];
    this.loadedModels = new Map(); // 모델 캐시

    // 바닥 색상들 (자연스러운 숲 테마)
    this.groundColors = [
      0x114411, // 진한 초록 (Forest Green)
      // 0x328d32, // 라임 그린 (Lime Green)
    ];

    // 오브젝트들을 카테고리별로 분리
    this.forestObjectTypes = {
      big: [
        // "Tree_1_A_Color1.gltf",
        // "Tree_2_A_Color1.gltf",
        // "Tree_3_A_Color1.gltf",
        // "Tree_4_A_Color1.gltf",
        // "Rock_1_N_Color1.gltf",
        // "Rock_1_O_Color1.gltf",
        // "Rock_1_P_Color1.gltf",
        // "Rock_1_Q_Color1.gltf",
        // "Rock_3_M_Color1.gltf",
        // "Rock_3_N_Color1.gltf",
        // "Rock_3_O_Color1.gltf",
        // "Rock_3_P_Color1.gltf",
        // "Rock_3_Q_Color1.gltf",
        // "Rock_3_R_Color1.gltf",
      ],
      medium: [
        // "Rock_1_G_Color1.gltf",
        // "Rock_1_H_Color1.gltf",
        // "Rock_1_I_Color1.gltf",
        // "Rock_1_J_Color1.gltf",
        // "Rock_1_K_Color1.gltf",
        // "Rock_1_L_Color1.gltf",
        // "Rock_1_M_Color1.gltf",
        // "Rock_3_G_Color1.gltf",
        // "Rock_3_H_Color1.gltf",
        // "Rock_3_I_Color1.gltf",
        // "Rock_3_J_Color1.gltf",
        // "Rock_3_K_Color1.gltf",
        // "Rock_3_L_Color1.gltf",
      ],
      small: [
        // "Rock_1_A_Color1.gltf",
        // "Rock_1_B_Color1.gltf",
        // "Rock_1_C_Color1.gltf",
        // "Rock_1_D_Color1.gltf",
        // "Rock_1_E_Color1.gltf",
        // "Rock_1_F_Color1.gltf",
        "Rock_3_A_Color1.gltf",
        "Rock_3_B_Color1.gltf",
        "Rock_3_C_Color1.gltf",
        // "Rock_3_D_Color1.gltf",
        // "Rock_3_E_Color1.gltf",
        // "Rock_3_F_Color1.gltf",
        "Grass_1_A_Color1.gltf",
        "Grass_1_B_Color1.gltf",
        "Grass_1_C_Color1.gltf",
        "Grass_2_A_Color1.gltf",
        "Grass_2_B_Color1.gltf",
        "Grass_2_C_Color1.gltf",
      ],
    };

    // 각 오브젝트 타입별 밀도 설정 (타일당 개수 범위)
    this.objectDensity = {
      big: { min: 0, max: 0 },
      medium: { min: 0, max: 0 },
      small: { min: 4, max: 8 },
    };
  }

  // 씬 초기화
  async init() {
    this.createLighting();
    await this.createEnvironment();
    Utils.log("씬 매니저 초기화 완료");
  }

  // 조명 설정
  createLighting() {
    // 환경광 (전체적으로 밝은 빛) - 그림자를 부드럽게 하기 위해 더 밝게
    this.lights.ambient = new THREE.AmbientLight(0x888888, 1.0);
    this.scene.add(this.lights.ambient);

    // 방향광 (태양빛 같은 효과) - 강도를 낮춰서 그림자를 부드럽게
    this.lights.directional = new THREE.DirectionalLight(0xffffff, 0.8);
    this.lights.directional.position.set(10, 30, 10); // 더 높은 위치
    this.lights.directional.castShadow = true;

    // 조명의 타겟 설정 (초기에는 원점을 향하도록)
    this.lights.directional.target.position.set(0, 0, 0);
    this.scene.add(this.lights.directional.target);

    // 그림자 품질 설정
    this.setupShadows(this.lights.directional);

    this.scene.add(this.lights.directional);
  }

  // 그림자 설정
  setupShadows(light) {
    light.shadow.mapSize.width = 2048; // 해상도를 적당히 낮춰서 부드럽게
    light.shadow.mapSize.height = 2048;
    light.shadow.camera.near = 0.5;
    light.shadow.camera.far = 200; // 더 넓은 범위로 확장
    light.shadow.camera.left = -120; // 훨씬 더 큰 그림자 범위
    light.shadow.camera.right = 120;
    light.shadow.camera.top = 120;
    light.shadow.camera.bottom = -120;

    // 그림자를 부드럽게 만드는 설정
    light.shadow.bias = -0.0005; // bias를 약간 증가시켜서 부드럽게
    light.shadow.normalBias = 0.05; // normalBias를 증가시켜서 더 부드럽게
    light.shadow.radius = 8; // 그림자 블러 효과 추가
    light.shadow.blurSamples = 25; // 블러 샘플 수 증가
  }

  // 환경 오브젝트 생성 (바닥 등)
  async createEnvironment() {
    // 무한 스크롤 바닥 생성
    await this.createInfiniteGround();
    // 초기 오브젝트 스폰
    await this.spawnInitialObjects();
  }

  // 무한 스크롤 바닥 생성
  async createInfiniteGround() {
    // 초기 타일들 생성 (3x3 그리드)
    for (let x = -1; x <= 1; x++) {
      for (let z = -1; z <= 1; z++) {
        this.createGroundTile(x, z);
      }
    }
  }

  // 개별 바닥 타일 생성
  createGroundTile(tileX, tileZ) {
    const planeGeometry = new THREE.PlaneGeometry(this.tileSize, this.tileSize);

    // 랜덤 색상 선택
    const randomColor =
      this.groundColors[Math.floor(Math.random() * this.groundColors.length)];

    const planeMaterial = new THREE.MeshLambertMaterial({
      color: randomColor,
      side: THREE.DoubleSide,
    });

    const tile = new THREE.Mesh(planeGeometry, planeMaterial);
    tile.rotation.x = -Math.PI / 2; // 평면을 수평으로 회전
    tile.position.set(tileX * this.tileSize, 0, tileZ * this.tileSize);
    tile.receiveShadow = true; // 그림자 받기

    // 타일 정보 저장 (색상도 포함)
    tile.userData = { tileX, tileZ, color: randomColor };

    this.groundTiles.push(tile);
    this.scene.add(tile);
  }

  // 초기 오브젝트 스폰
  async spawnInitialObjects() {
    // 모든 타일에 오브젝트 스폰
    for (const tile of this.groundTiles) {
      await this.spawnObjectsOnTile(tile.userData.tileX, tile.userData.tileZ);
    }
  }

  // 특정 타일에 오브젝트 스폰
  async spawnObjectsOnTile(tileX, tileZ) {
    // 각 카테고리별로 오브젝트 스폰
    for (const [objectType, objects] of Object.entries(
      this.forestObjectTypes
    )) {
      const density = this.objectDensity[objectType];
      const objectCount =
        Math.floor(Math.random() * (density.max - density.min + 1)) +
        density.min;

      for (let i = 0; i < objectCount; i++) {
        // 해당 카테고리에서 랜덤 오브젝트 선택
        const randomObject =
          objects[Math.floor(Math.random() * objects.length)];

        // 타일 내 랜덤 위치
        const x =
          tileX * this.tileSize + (Math.random() - 0.5) * this.tileSize * 0.8;
        const z =
          tileZ * this.tileSize + (Math.random() - 0.5) * this.tileSize * 0.8;

        await this.spawnObject(randomObject, x, z, tileX, tileZ, objectType);
      }
    }
  }

  // 개별 오브젝트 스폰
  async spawnObject(modelName, x, z, tileX, tileZ, objectType) {
    try {
      // 모델 캐시에서 확인
      let model;
      if (this.loadedModels.has(modelName)) {
        model = this.loadedModels.get(modelName).clone();
      } else {
        // 새로 로드
        const loader = new THREE.GLTFLoader();
        const gltf = await new Promise((resolve, reject) => {
          loader.load(
            `models/forest/Assets/gltf/${modelName}`,
            resolve,
            undefined,
            reject
          );
        });

        // 캐시에 저장
        this.loadedModels.set(modelName, gltf.scene);
        model = gltf.scene.clone();
      }

      model.scale.set(0.8, 0.8, 0.8);

      // 위치 설정
      model.position.set(x, 0, z);

      // 랜덤 회전
      model.rotation.y = Math.random() * Math.PI * 2;

      // 그림자 설정
      model.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          // child.receiveShadow = true;
        }
      });

      // 타일 정보 저장 (오브젝트 타입도 포함)
      model.userData = { tileX, tileZ, modelName, objectType };

      this.environmentObjects.push(model);
      this.scene.add(model);
    } catch (error) {
      console.warn(`오브젝트 로드 실패: ${modelName}`, error);
    }
  }

  // 카메라 기반 무한 스크롤 업데이트
  updateInfiniteWorld(playerPosition, camera) {
    // 조명과 그림자 카메라를 카메라 위치에 맞춰 업데이트
    this.updateShadowCamera(camera.position);

    // 카메라 기반으로 필요한 타일들 계산
    const neededTiles = this.calculateVisibleTiles(camera);

    // 현재 존재하는 타일들
    const existingTiles = new Set();
    this.groundTiles.forEach((tile) => {
      existingTiles.add(`${tile.userData.tileX},${tile.userData.tileZ}`);
    });

    // 제거할 타일들 (카메라 시야에서 벗어난 타일들)
    this.groundTiles = this.groundTiles.filter((tile) => {
      const key = `${tile.userData.tileX},${tile.userData.tileZ}`;
      if (!neededTiles.has(key)) {
        this.scene.remove(tile);
        this.removeObjectsFromTile(tile.userData.tileX, tile.userData.tileZ);
        return false;
      }
      return true;
    });

    // 새로 생성할 타일들
    neededTiles.forEach(async (key) => {
      if (!existingTiles.has(key)) {
        const [x, z] = key.split(",").map(Number);
        // 랜덤 색상으로 타일 생성
        this.createGroundTile(x, z);
        await this.spawnObjectsOnTile(x, z);
      }
    });
  }

  // 카메라 시야에 보이는 타일들 계산 (Orthographic 카메라용)
  calculateVisibleTiles(camera) {
    const neededTiles = new Set();

    // 카메라 위치 정보
    const cameraPosition = camera.position;

    // Orthographic 카메라의 뷰 범위 계산
    const viewLeft = camera.left;
    const viewRight = camera.right;
    const viewTop = camera.top;
    const viewBottom = camera.bottom;

    // 카메라가 보는 월드 좌표 범위 계산 (Y=0 평면에 투영)
    const worldLeft = cameraPosition.x + viewLeft;
    const worldRight = cameraPosition.x + viewRight;
    const worldTop = cameraPosition.z + viewTop;
    const worldBottom = cameraPosition.z + viewBottom;

    // 여유분 추가 (화면 경계에서 갑자기 나타나는 것 방지)
    const buffer = this.tileSize * 2;
    const bufferLeft = worldLeft - buffer;
    const bufferRight = worldRight + buffer;
    const bufferTop = worldTop + buffer;
    const bufferBottom = worldBottom - buffer;

    // 필요한 타일 범위 계산
    const startTileX = Math.floor(bufferLeft / this.tileSize);
    const endTileX = Math.ceil(bufferRight / this.tileSize);
    const startTileZ = Math.floor(bufferBottom / this.tileSize);
    const endTileZ = Math.ceil(bufferTop / this.tileSize);

    // 타일 생성
    for (let x = startTileX; x <= endTileX; x++) {
      for (let z = startTileZ; z <= endTileZ; z++) {
        neededTiles.add(`${x},${z}`);
      }
    }

    return neededTiles;
  }

  // 조명과 그림자 카메라를 카메라 위치에 맞춰 업데이트
  updateShadowCamera(cameraPosition) {
    if (
      this.lights.directional &&
      this.lights.directional.shadow &&
      this.game &&
      this.game.camera
    ) {
      // 그림자 카메라를 카메라 중심으로 이동
      const shadowCamera = this.lights.directional.shadow.camera;

      // 카메라가 바라보는 방향을 계산 (카메라의 lookAt 방향)
      const cameraDirection = new THREE.Vector3();
      this.game.camera.getWorldDirection(cameraDirection);

      // 조명을 카메라 뒤쪽 위에서 카메라가 바라보는 방향으로 비추도록 설정
      // 카메라의 오프셋을 고려하여 조명 위치 계산
      const lightOffset = new THREE.Vector3(0, 25, 15); // 카메라 뒤쪽 위
      const targetPosition = new THREE.Vector3()
        .copy(cameraPosition)
        .sub(cameraDirection.multiplyScalar(25)) // 카메라 뒤쪽으로 25만큼
        .add(lightOffset);

      // 조명 위치를 부드럽게 interpolate (선형 보간) - 더 빠르게 반응
      this.lights.directional.position.lerp(targetPosition, 0.15);

      // 조명이 카메라가 바라보는 지점을 향하도록 설정
      const lookAtTarget = new THREE.Vector3()
        .copy(cameraPosition)
        .add(cameraDirection.multiplyScalar(30)); // 카메라 앞쪽 30만큼

      this.lights.directional.target.position.lerp(lookAtTarget, 0.15);
      this.lights.directional.target.updateMatrixWorld();

      // 그림자 카메라도 카메라를 중심으로 설정 (더 큰 범위)
      const shadowRange = 120; // 그림자 범위를 더 넓게
      shadowCamera.left = cameraPosition.x - shadowRange;
      shadowCamera.right = cameraPosition.x + shadowRange;
      shadowCamera.top = cameraPosition.z + shadowRange;
      shadowCamera.bottom = cameraPosition.z - shadowRange;

      // 그림자 카메라 업데이트
      shadowCamera.updateProjectionMatrix();
    }
  }

  // 특정 타일의 오브젝트들 제거
  removeObjectsFromTile(tileX, tileZ) {
    this.environmentObjects = this.environmentObjects.filter((obj) => {
      if (obj.userData.tileX === tileX && obj.userData.tileZ === tileZ) {
        this.scene.remove(obj);
        return false;
      }
      return true;
    });
  }

  // 환경 오브젝트 목록 반환 (충돌 검사용)
  getEnvironmentObjects() {
    return this.environmentObjects;
  }

  // 플레이어에게 환경 오브젝트 참조 제공
  setupCollisionForPlayer(player) {
    player.setEnvironmentObjects(this.environmentObjects);
  }
}

// webpack 빌드를 위해 default export 추가
export default SceneManager;